
/* eslint-disable no-console */

import { Component, Vue, Watch } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import { mixins } from 'vue-class-component';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import { UserConfModule } from '@/store/modules/userConf';
import { ThemeConfModule } from '@/store/modules/theme';
import TableOfContents from '@/types/TableOfContents';
import Bookmark from '@/models/Bookmark';
import Sidebar from '@/components/Sidebar.vue';
import { debounce } from '@/utils/functions';
import ThemeConf from '@/types/ThemeConf';
import { applyStyle } from '@/utils/changeStyle';
import Timer from 'easytimer.js';
import { AccountInfo, AccountIdentity } from '@/types/AccountInfo';
import { restoreFromLocalStorage } from '@/store/plugins/SaveUserConf';
import { CUSTOM_TERM_FILTER, LOCK_CONST } from '@/constants';
import Scorm from '@bob-the-builder/kb4-scorm-client';
import Testout from '@/components/Testout.vue';
import TrainingNav from '@/components/TrainingNav.vue';
const AppState = getModule(AppModule, store);
const UserConfState = getModule(UserConfModule, store);
const ThemeStore = getModule(ThemeConfModule, store);
import VueRouter from 'vue-router';
import kb4ScormClientHelper from '@/utils/kb4ScormClientHelper';

@Component({ components: { TrainingNav, Sidebar, Testout } })
export default class Training extends mixins(Scorm) {
  toc: TableOfContents = AppState.tableOfContents;
  lockBookmark: boolean = false;
  showDisclaimer: boolean = true;
  showTestoutModal: boolean = true;
  lostConnection: boolean = false;
  lostScormConnection: boolean = false;
  theme: ThemeConf | null = null;
  loadingTheme: boolean = false;

  public $refs!: {
    mainContent: HTMLFormElement;
    slideTitle: HTMLFrameElement;
    disclaimer: HTMLFormElement;
    disclaimerDialog: HTMLFormElement;
    testOutDialog: HTMLFormElement;
    bookmarkYes: HTMLFormElement;
    disclaimerClose: HTMLFormElement;
    modalConnection: HTMLElement;
    toogleSidebar: HTMLElement;
  };

  public created() {
    this.init(ThemeStore.getScormOptions);

    this.log('BTB.conf', AppState.BTB_CONF);

    const resizeEnd = debounce(() => {
      // This debounce will prevent commits to vuex store until resizing is completed.
      this.handleResize();
      this.setDocHeight();
    }, 250);

    window.addEventListener('resize', resizeEnd);
    window.addEventListener('pagehide', this.beforeFinish);

    AppState.setUserId(this.getUserId());
    document.addEventListener('fullscreenchange', this.onFullscreenChange);

    // Needed to support IE11
    document.addEventListener('MSFullscreenChange', this.onFullscreenChange);
    
    this.checkForPreviousAttempts();
    this.applyThemeAndGoToBookmark();

    this.restoreUserConfFromLocalStorage();

    store.watch(
      () => AppState.getCurrentIndex,
      () => {
        this.updateTitle();
        this.triggerFocusOnSlideTitle();
        if (this.testoutIndex < 0) 
          if (!(AppState.getCurrentIndex === 0 && this.showTestout)) {
            this.closeDisclaimer();
          }
      }
    );
    this.updateTitle();
    AppState.updateData();

    this.triggerFocusDisclaimer();
    this.lostConnectionChecker();
  }

  mounted() {
    if (this.lostConnection) this.triggerFocusOnLostConnectionsModal();

    window.addEventListener('orientationchange', this.setDocHeight);
    this.setDocHeight();
    setTimeout(this.setDocHeight, 500);
  }

  checkForPreviousAttempts() {
    const bookmark = this.getBookmark();
    if (bookmark.testout?.failedTestout) AppState.setFailedTestout();
  }

  get isScenarioPageActive() {
    return AppState.getIsScenarioPageActive;
  }

  get routeQuery(): string {
    if (!AppState.BTB_CONF.debug) return '';

    const slide = this.$route?.query?.slide;
    if (typeof slide === 'string') return slide;
    return '';
  }

  @Watch('routeQuery', { immediate: true })
  onRouteChange(newSlideId: string) {
    if (!AppState.BTB_CONF.debug) return;

    if (newSlideId) {
      if (newSlideId === this.getCurrentSlide) return;
      AppState.goTo([newSlideId, this.getSlideIndexById(newSlideId), true]);
    } else this.updateRoute(this.getCurrentSlide);
  }

  @Watch('getCurrentSlide')
  onCurrentSlideChange(newSlide: string): void {
    if (!AppState.BTB_CONF.debug) return;
    if (newSlide !== this.routeQuery) this.updateRoute(newSlide);
  }

  updateRoute(slide: string) {
    this.$router.push({
      path: `/${slide}`
    });
  }

  beforeDestroy() {
    window.removeEventListener('orientationchange', this.setDocHeight);
  }

  public async applyThemeAndGoToBookmark() {
    if (Vue.config.productionTip) this.loadingTheme = true;

    this.theme = await this.getThemeSettings();

    ThemeStore.setTheme(this.theme);
    this.applyThemeHelper();
  }

  public restoreUserConfFromLocalStorage() {
    restoreFromLocalStorage(
      (store.state as any).userConf,
      store.getters.storagePrefix
    );
  }

  public clientIsApp(){
    return kb4ScormClientHelper.isMobileApp();
  }

  public applyThemeHelper() {
    if (this.theme) {
      applyStyle(this.theme);
      if (this.theme.slides?.intro)
        AppState.addIntro(this.theme.slides.intro.heading);
      if (this.theme.slides?.outro)
        AppState.replaceOutro(this.theme.slides.outro.heading);
      if (this.theme.slides?.hide) 
        AppState.hideSlides(this.theme.slides.hide);
      if (this.theme.slides?.insertSlide)
        AppState.addInsertSlide(this.theme.slides.insertSlide);
      if (AppState.isDebug && AppState.tableOfContents.referenceMaterial?.id)
        AppState.addDebugInsertSlide();
    }
    this.loadingTheme = false;
    this.goToBookmarkOrInvokePopup();
  }

  public beforeFinish() {
    const isTestoutSessionActive = AppState.getIsTestout && !AppState.failedTestout;
    const bookmark = this.getBookmark();
    let newBookmark = new Bookmark(
      AppState.getCurrentIndex,
      AppState.getCurrentSlideUid,
      AppState.getScore,
      AppState.getTotalScore,
      AppState.getBiggestIndex,
      bookmark.version,
      AppState.getSavedData,
      bookmark.testout,
      isTestoutSessionActive
    );

    if(isTestoutSessionActive){ 
      //in the case the window is closed during a testout session or at the last page
      newBookmark.currentIndex = bookmark.currentIndex;
      newBookmark.currentIndexUid = bookmark.currentIndexUid;
      bookmark.biggestIndex = this.testoutIndex;
    }

    try {
      this.setBookmark(newBookmark);
      this.finish();
    } catch (error) {
      console.log(error);
    }
  }

  lostConnectionChecker() {
    if (this.clientIsApp()) {
      this.lostConnection = false;
      return;
    }
    
    this.lostConnection = !navigator.onLine;

    setTimeout(() => {
      this.lostConnectionChecker();
    }, 1000);
  }

  public onLostConnection(error?: string) {
    if (Vue.config.productionTip && !this.lostScormConnection) {
      console.error('API Connection Lost');
      this.lostScormConnection = true;
      this.showDisclaimer = false;
      this.showTestoutModal = false;
      setTimeout(() => {
        this.$refs.modalConnection.focus();
      }, 300);
    }
  }

  public goToBookmarkOrInvokePopup() {
    const bookMark = this.getBookmark();
    if (!this.hasSCORMBookmark()) {
      AppState.cleanData();
      if (bookMark.currentIndex === 0 && this.routeQuery.length)
        bookMark.currentIndex = this.getSlideIndexById(this.routeQuery);
      AppState.goToBookmark(bookMark);
      AppState.setHasStarted(true);
    } else {
      AppState.fixScores(bookMark);
      this.callBookmarkPopup(bookMark);
    }
  }

  getSlideIndexById(id: string): number {
    return AppState.getTableOfContents.slides.findIndex(
      slide => slide.id === id
    );
  }

  private setDocHeight() {
    document.documentElement.style.setProperty(
      '--vh',
      `${window.innerHeight / 100}px`
    );
    document.documentElement.style.setProperty(
      '--dvh',
      `${window.innerHeight}px`
    );
  }

  private closeTraining() {
    window.close();
  }

  get slideToRender(): string {
    if (AppState.BTB_CONF.debug) return this.routeQuery;
    return this.getCurrentSlide;
  }

  get showingTestoutModal(): boolean{
    return this.showTestoutModal && this.showTestout;
  }

  get showTestout(): boolean { 
    const bookMark = this.getBookmark();
    if (this.testoutIndex < 0) return Boolean(this.theme?.test_out && !bookMark.testout?.failedTestout);
    return Boolean(this.theme?.test_out && bookMark.testout && Object.keys(bookMark.testout).length === 0 && (this.testoutIndex === this.getCurrentIndex));
  }

  get testoutIndex() {
    return this.toc.slides.findIndex(({testoutprompt}) => testoutprompt);
  }

  get showSidebar() {
    return AppState.getSideBarOpen;
  }

  get logo() {
    if (this.theme && this.theme.logo) return this.theme.logo;
    else return '';
  }

  get getCurrentIndex() {
    return AppState.currentIndex;
  }

  get getCurrentSlide() {
    return AppState.currentSlide;
  }

  get isFirst(): boolean {
    return AppState.currentSlide === this.toc.slides[0].id;
  }

  get isLast(): boolean {
    return (
      AppState.currentSlide === this.toc.slides[this.toc.slides.length - 1].id
    );
  }

  get isBackwardsDisabled(): boolean {
    return (
      this.isFirst ||
      this.isTotallyLocked ||
      AppState.isTestoutSessionActive
    );
  }

  get isForwardDisabled(): boolean {
    return this.isLast || AppState.isLockedForward;
  }

  get staticButtonValue() {
    return UserConfState.staticButton;
  }

  get isTotallyLocked(): boolean {
    return AppState.isTotallyLocked;
  }

  get canClose() {
    return !!window.opener;
  }

  private closeSideBar() {
    AppState.closeSideBar();
    setTimeout(() => {
      this.$refs.toogleSidebar?.focus();
    }, 500);
  }

  private openSidebar() {
    AppState.openSideBar();
  }

  private updateTitle(): void {
    if (this.slideTitle.indexOf(CUSTOM_TERM_FILTER) > -1) {
      // Has a custom term
      let filteredTitle = this.slideTitle
        .split(`' ${CUSTOM_TERM_FILTER}`)[0]
        .substring(1);
      document.title = `${
        this.toc.name
      } - ${(this as any).$options.filters.customTerm(filteredTitle)}`;
      return;
    }
    document.title = `${this.toc.name} - ${this.slideTitle}`;
  }

  get slideTitle(): string {
    return this.toc.slides[this.getCurrentIndex].title;
  }

  private next() {
    if (this.isForwardDisabled) return;
    AppState.setHasStarted(true);
    AppState.increment(1);
    this.updateBookmark();
    if (this.isLast)
      this.setResults(AppState.getScore, AppState.getTotalScore, () => {});
    if (AppState.getIsMobile) this.closeSideBar();
  }

  private previous() {
    if (this.isFirst || this.isTotallyLocked) return;
    AppState.increment(-1);
    this.updateBookmark();
    if (AppState.getIsMobile) this.closeSideBar();
  }

  private updateBookmark(bkmrk?: Bookmark) {
    const bookmark = this.getBookmark();
    let newBookmark = {
      ...new Bookmark(
        AppState.getCurrentIndex,
        AppState.getCurrentSlideUid,
        AppState.getScore,
        AppState.getTotalScore,
        AppState.getBiggestIndex,
        bookmark.version,
        bookmark.lastSlideState,
        bookmark.testout
      )
    };
    if (bkmrk) newBookmark = { ...newBookmark, ...bkmrk };
    this.setBookmark(newBookmark);
  }

  private focusOnMain(e?: any) {
    e.preventDefault();
    this.triggerFocusOnMain();
  }

  private triggerFocusOnBookmarkPopup() {
    setTimeout(() => {
      if (this.$refs.bookmarkYes) this.$refs.bookmarkYes.focus();
    }, 500);
  }

  private triggerFocusDisclaimer() {
    setTimeout(() => {
      
      if (this.$refs.disclaimerDialog) this.$refs.disclaimerDialog.focus();
      if (this.$refs.testOutDialog) this.$refs.testOutDialog.focus();
      if (this.$refs.disclaimer && !this.showTestout) this.$refs.disclaimer.focus();

    }, 500);
  }

  private triggerFocusOnSlideTitle() {
    if (AppState.hasStarted && !this.showingTestoutModal)
      setTimeout(() => {
        this.$refs.slideTitle.focus();
      }, 500);
  }

  private triggerFocusOnMain() {
    setTimeout(() => {
      this.$refs.mainContent.focus();
      let firstChild = this.$refs.mainContent.children[0];
      if (firstChild) {
        let secondChild = firstChild.children[0] as HTMLElement;
        secondChild?.focus();
        secondChild?.querySelector('legend')?.focus();
      }
    }, 500);
  }

  private triggerFocusOnLostConnectionsModal() {
    this.$refs.modalConnection.focus();
  }

  private handleResize() {
    AppState.updateWindowWidth(window.innerWidth);
  }

  public goToFirstPage() {
    let bkmrk: Bookmark = this.getBookmark();
    bkmrk.currentIndex = 0;
    bkmrk.currentIndexUid = AppState.getTableOfContents.slides[0].uuid ?? '';
    const isTestoutSession = Boolean(
      bkmrk.testout?.usedTestout && !bkmrk.testout?.failedTestout
    );
    if (isTestoutSession) {
      this.showDisclaimer = true;
      this.showTestoutModal = true;
    }
    this.updateBookmark(bkmrk);
    AppState.goToBookmark(bkmrk);
    this.lockBookmark = false;
    AppState.setHasStarted(true);
    AppState.setLock(LOCK_CONST.UNLOCKED);
  }

  public goToBookmark(bookMark: Bookmark) {
    AppState.goToBookmark(bookMark);
    this.lockBookmark = false;
    this.updateBookmark();
    AppState.setHasStarted(true);
  }
  public getPreviousValidSlideIndex(uid: string): number {
    const initialSlides = AppState.initialTableOfContents.slides;

    //get slide's original index
    const userSlideIndex = initialSlides.findIndex(slide => slide.uuid === uid);
    //get uid of previous slide that isn't hidden
    let validUid = AppState.getTableOfContents.slides[0].uuid;
    let iterator = userSlideIndex - 1;
    while (iterator > 0)
      if (this.isSlideHidden(initialSlides[iterator].uuid ?? '')) iterator--;
      else {
        validUid = initialSlides[iterator].uuid;
        break;
      }

    //find the index of validUid in updated ToC slides
    return this.getSlideIndexByUid(validUid ?? '');
  }

  public isSlideHidden(uid: string): boolean {
    return Boolean(this.theme?.slides?.hide?.includes(uid));
  }

  public getSlideIndexByUid(uid: string): number {
    return AppState.getTableOfContents.slides.findIndex(
      slide => slide.uuid === uid
    );
  }

  public callBookmarkPopup(bookmark: Bookmark) {
    if (bookmark.currentIndex > 0 && this.toc.slides[bookmark.currentIndex]) {
      //check if there's a saved uid
      if (bookmark.currentIndexUid) {
        if (this.isSlideHidden(bookmark.currentIndexUid))
          //the slide has been hidden, must get previous valid slide
          bookmark.currentIndex = this.getPreviousValidSlideIndex(
            bookmark.currentIndexUid
          );
        //if a previous slide wass hidden causing indexes to shift, this makes sure we get the correct slide
        else
          bookmark.currentIndex = this.getSlideIndexByUid(
            bookmark.currentIndexUid
          );
        bookmark.biggestIndex = bookmark.currentIndex;
      }
      AppState.goToBookmark(bookmark);
      this.lockBookmark = true;
      this.showDisclaimer = false;
      this.showTestoutModal = false;
      AppState.setHasStarted(false);
      this.triggerFocusOnBookmarkPopup();
    }
  }

  public closeBookmarkPopup(): void {
    this.lockBookmark = false;
  }

  private onFullscreenChange(e: any) {
    AppState.toggleFullscreen();
  }
  public closeDisclaimer() {
    this.showDisclaimer = false;
    this.triggerFocusOnMain();
  }
  public closeTestout() {
    this.showTestoutModal = false;
    this.triggerFocusOnMain();
  }
  public cancelTestout() {
    this.showDisclaimer = false;
    this.showTestoutModal = false;
    AppState.setTestout(false);
  }
}
